import React, { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import * as Tooltip from '@radix-ui/react-tooltip';
import * as Select from '@radix-ui/react-select';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { HandThumbUpIcon, HandThumbDownIcon, ChevronDownIcon } from '@heroicons/react/24/solid';
import { useFeedback } from '../../utils/feedback';
import { FeedbackCategory, FeedbackSentiment } from '../../types/api';

const categoryOptions = Object.entries(FeedbackCategory).map(([key, value]) => ({
  label: key === 'UI' ? 'UI' : key.charAt(0) + key.slice(1).toLowerCase(),
  value: value
}));

const Feedback = ({ 
  position = 'bottom',
  prompt = "What do you think about this feature?",
  source = "general",
  initialCategory = FeedbackCategory.UI,
  onSubmit 
}) => {
  const { handleFeedbackSubmit } = useFeedback();
  const [isOpen, setIsOpen] = useState(false);
  const [rating, setRating] = useState(null);
  const [comment, setComment] = useState('');
  const [category, setCategory] = useState(initialCategory);
  const [requiresFollowup, setRequiresFollowup] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async () => {
    try {
      await handleFeedbackSubmit({
        rating,
        comment,
        contactConsent: requiresFollowup,
        source,
        category
      });

      onSubmit?.({
        rating,
        comment,
        contactConsent: requiresFollowup,
        source,
        category
      });
      
      setIsSubmitted(true);
      
      setTimeout(() => {
        setIsOpen(false);
        setIsSubmitted(false);
        setRating(null);
        setComment('');
        setCategory(initialCategory);
        setRequiresFollowup(false);
      }, 2000);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    }
  };

  return (
    <div className={`inline-flex ${position === 'right' ? 'ml-2' : 'mt-2'}`}>
      <Tooltip.Provider>
        <Tooltip.Root>
          <Tooltip.Trigger asChild>
            <Dialog.Root open={isOpen} onOpenChange={setIsOpen}>
              <Dialog.Trigger asChild>
                <button
                  className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
                  aria-label="Give feedback"
                >
                  <QuestionMarkCircleIcon className="w-5 h-5" />
                  <span>Feedback</span>
                </button>
              </Dialog.Trigger>

              <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 bg-black/50" />
                <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-6 w-[400px] max-w-[90vw]">
                  {!isSubmitted ? (
                    <>
                      <Dialog.Title className="text-lg font-semibold mb-4">
                        {prompt}
                      </Dialog.Title>
                      
                      <div className="space-y-4">
                        {/* Category Selection */}
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Category
                          </label>
                          <Select.Root value={category} onValueChange={setCategory}>
                            <Select.Trigger className="w-full inline-flex items-center justify-between px-3 py-2 border rounded-md text-sm">
                              <Select.Value />
                              <Select.Icon>
                                <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                              </Select.Icon>
                            </Select.Trigger>
                            <Select.Portal>
                              <Select.Content className="bg-white rounded-md shadow-lg border">
                                <Select.Viewport className="p-1">
                                  {categoryOptions.map(({ label, value }) => (
                                    <Select.Item
                                      key={value}
                                      value={value}
                                      className="relative flex items-center px-8 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                                    >
                                      <Select.ItemText>{label}</Select.ItemText>
                                    </Select.Item>
                                  ))}
                                </Select.Viewport>
                              </Select.Content>
                            </Select.Portal>
                          </Select.Root>
                        </div>

                        {/* Rating Buttons */}
                        <div className="flex justify-center gap-4">
                          <button
                            onClick={() => setRating('positive')}
                            className={`p-2 rounded-full ${
                              rating === 'positive' 
                                ? 'bg-green-100 text-green-600' 
                                : 'text-gray-400 hover:text-gray-600'
                            }`}
                            aria-label="Positive feedback"
                          >
                            <HandThumbUpIcon className="w-6 h-6" />
                          </button>
                          <button
                            onClick={() => setRating('negative')}
                            className={`p-2 rounded-full ${
                              rating === 'negative' 
                                ? 'bg-red-100 text-red-600' 
                                : 'text-gray-400 hover:text-gray-600'
                            }`}
                            aria-label="Negative feedback"
                          >
                            <HandThumbDownIcon className="w-6 h-6" />
                          </button>
                        </div>

                        <textarea
                          value={comment}
                          onChange={(e) => setComment(e.target.value)}
                          placeholder="Tell us more (optional)"
                          className="w-full p-2 border rounded-md h-24 resize-none"
                          aria-label="Feedback details"
                        />

                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={requiresFollowup}
                            onChange={(e) => setRequiresFollowup(e.target.checked)}
                            className="rounded border-gray-300"
                          />
                          <span className="text-sm text-gray-600">
                            I would like to be contacted about this feedback
                          </span>
                        </label>

                        <div className="flex justify-end gap-2">
                          <Dialog.Close asChild>
                            <button className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">
                              Cancel
                            </button>
                          </Dialog.Close>
                          <button
                            onClick={handleSubmit}
                            disabled={!rating}
                            className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Submit
                          </button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-lg font-medium text-gray-900">
                        Thanks for your feedback!
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        We're working to make this better.
                      </p>
                    </div>
                  )}
                </Dialog.Content>
              </Dialog.Portal>
            </Dialog.Root>
          </Tooltip.Trigger>
          <Tooltip.Portal>
            <Tooltip.Content
              className="bg-gray-900 text-white px-3 py-2 rounded text-sm"
              sideOffset={5}
            >
              Let us know how we can improve this feature
              <Tooltip.Arrow className="fill-gray-900" />
            </Tooltip.Content>
          </Tooltip.Portal>
        </Tooltip.Root>
      </Tooltip.Provider>
    </div>
  );
};

export default Feedback; 